import jsPsychSurveyMultiSelect from '@jspsych/plugin-survey-multi-select';
import jsPsychSurveyMultiChoice from '@jspsych/plugin-survey-multi-choice';
import jsPsychSurveyText from '@jspsych/plugin-survey-text';
import store from 'store2';
import { processSurveyData } from '../helperFunctions';

const consent_roar = {
  type: jsPsychSurveyMultiSelect,
  questions: [
    {
      prompt: ` <div>
        <h4> STANFORD UNIVERSITY CONSENT FORM </h4>
        <p class="consent_form_text">
        <b>PURPOSE OF THE STUDY</b> 
        <br>
        Data collected through games in the web-browser will help researchers understand relationships between academic skills, reading proficiency, cognition, perception, and/or attitudes towards reading and school in individuals with a broad range of reading skills.
        <br><br>
        <b>STUDY PROCEDURES</b> 
        <br>
        In this study, you will be asked to complete computer tasks via a computer screen. Audio will be presented via headphones or speakers.
        <br><br>
        <b>PRIVACY AND DATA COLLECTION</b> <br>
        We will do our best to ensure your privacy. Data that is collected through this online experiment is stored separately from identifying information such as your name. For the sake of payment, sometimes we store an email address you provide, but this is stored separately from the responses that are recorded in the online experiment. Each participant is assigned a code and that is used rather than names. This is called “coded data” and we try to ensure that the identity of our research participants is kept confidential. Data collected as part of this study may be used for many years to help discover trends in the population and explore changes due to development and education. In addition, coded data may be shared online or with collaborators to allow for new and unforeseen discoveries. Researchers may choose to include coded data in publications to support findings, or they may choose to release coded data alongside findings for replicability.
        <br>
        <br>
        We will collect mouse and click, scores earned, button presses and their timestamps, or other data that may be derived from your behavior on our page. This data will be stored on servers. Incomplete data may be logged if you quit out of the experiment early. If you would like to void your data, you may request it through our contact email. 
        <br>
        <br>
        <b>COMPENSATION</b>
        <br> 
        Participation in this study is voluntary and you will not receive financial compensation.
        <br>
        <br>
        <b>RISKS, STRESS, OR DISCOMFORT</b>
        <br>
        If there is any reason to believe you are not safe to participate in any of the tasks, please contact us at <a href="url">readingresearch@stanford.edu</a>. Some people may experience some physical discomfort or boredom due to being asked to sit for long periods. For computer tasks, some people may also experience dry eyes or eye fatigue. For some tasks that are untimed, breaks can be taken as needed during the session.
        <br>
        <br>
        <b>CONTACT INFORMATION </b>
        <br>
        If you have any additional questions or concerns about our research, feel free to email us at <a href="url">readingresearch@stanford.edu</a>. We will be more than happy to help!
        <br>
        <br>
        For general information regarding questions or concerns about [your/your child’s] rights as a research participant, please call 1-866-680-2906 to reach the Administrative Panel on Human Subjects in Medical Research, Stanford University.
        </p>
 </div>
     `,
      options: [
        `<b>I agree to participate in this research. Participation in this research is voluntary, and I can stop at any time without penalty. <br> I feel that I understand what I am getting into, and I know I am free to leave the experiment at any time by simply closing the web browser.
</b>`,
      ],
      required: true,
      required_message: 'You must check the box to continue',
      name: 'Agree',
    },
  ],
};

const demo_survey = {
  type: jsPsychSurveyMultiChoice,
  preamble: () => `
    <div id="kidConsent">
        <h1> Please share a bit more to help us understand your data! [OPTIONAL] </h1>
    </div>`,
  questions: [
    {
      prompt: '<b>HOW OLD ARE YOU?</b>',
      name: 'age',
      options: ['2', '3', '4', '5', '6', '7', '8', '9', '10+', 'Adult'],
      required: false,
      horizontal: true,
    },
    {
      prompt: '<b>DO YOU SPEAK ENGLISH AT HOME?</b>',
      name: 'ell',
      options: ['YES', 'NO'],
      required: false,
      horizontal: true,
    },
    {
      prompt: '<b>HAVE YOU PLAYED THIS GAME BEFORE?</b>',
      name: 'retake',
      options: ['YES', 'NO'],
      required: false,
      horizontal: true,
    },
  ],
  button_label: ["LET'S PLAY!"],
  on_finish: (data) => {
    processSurveyData(data);
  },
};

const consent_museum = {
  type: jsPsychSurveyMultiChoice,
  preamble: () => `
    <div id="kidConsent">
      <p id="notice" style="font-size: 3vh;"><b>
        Please note: this game is only for one person at a time! <br> Please let your child play by themselves.</b>
      </p>
      <p id="consentForm" style=" font-size: 2vh; text-align: left"> By proceeding, you and your child agree to participate in this research. Your child will be asked to draw or guess pictures of common objects on this tablet.  No audio/video taping will occur and no identifiable information will be collection. Your child's participation will take approximately 30 seconds – 5 minutes, depending on how long they choose to participate. The guessing/drawing data collected here will be used for research purposes by the Stanford Language and Cognition Lab and the San Jose Children's Discovery Museum, and will be displayed on the Museum's website on a future date. There are no risks or benefits to participating: no identifying information will be collected, so you and your child's identity will remain anonymous. Your child can stop drawing at any time or choose not to answer any question without penalty. For more information, email the Stanford lab at langcoglab@stanford.edu, or call 650-721-9270. If you are not satisfied with how this study is being conducted, or if you have any concerns, complaints, or general questions about the research or your rights as a participant, please contact the Stanford Institutional Review Board (IRB) to speak to someone independent of the research team at (650)-723-2480 or toll free at 1-866-680-2906.  You can also write to the Stanford IRB, Stanford University, 3000 El Camino Real, Five Palo Alto Square, 4th Floor, Palo Alto, CA 94306.
      </p>
    </div>`,
  questions: [
    {
      prompt: '<b>HOW OLD ARE YOU?</b>',
      name: 'age',
      options: ['2', '3', '4', '5', '6', '7', '8', '9', '10+', 'Adult'],
      required: true,
      horizontal: true,
    },
    {
      prompt: '<b>DO YOU SPEAK ENGLISH AT HOME?</b>',
      name: 'ell',
      options: ['YES', 'NO'],
      required: false,
      horizontal: true,
    },
    {
      prompt: `<b>CAN WE SAVE YOUR GUESSES?</b>`,
      name: 'saveAnswer',
      options: ['YES', 'NO'],
      required: true,
      horizontal: true,
    },
  ],
  button_label: ["LET'S PLAY!"],
  on_finish: (data) => {
    processSurveyData(data);
  },
};

const getLabId = {
  type: jsPsychSurveyText,
  questions: [
    {
      prompt: 'Lab ID:',
      name: 'labId',
      required: true,
    },
  ],
  css_classes: ['jspsych-content-modified'],
  on_finish: (data) => {
    const config = store.session.get('config');
    config.labId = data.response.labId;
    store.session.set('config', config);
  },
};

const ifGetLabId = {
  timeline: [getLabId],
  conditional_function: () =>
    !store.session.get('config').labId && store.session.get('config').recruitment === 'otherLabs',
};

const getPid = {
  type: jsPsychSurveyText,
  questions: [
    {
      prompt: 'Participant ID:',
      name: 'pid',
      required: true,
    },
  ],
  css_classes: ['jspsych-content-modified'],
  on_finish: (data) => {
    const config = store.session.get('config');
    config.pid = data.response.pid;
    store.session.set('config', config);
  },
};

const ifGetPid = {
  timeline: [getPid],
  conditional_function: () =>
    (!store.session.get('config').pid && store.session.get('config').recruitment === 'otherLabs') ||
    store.session.get('config').recruitment === 'validation',
};

const if_consent_form_museum = {
  timeline: [consent_museum],
  conditional_function: () => Boolean(store.session.get('config').recruitment === 'cdm'),
};

const if_consent_form_roar = {
  timeline: [consent_roar, demo_survey],
  conditional_function: () =>
    (store.session.get('config').recruitment === 'demo' ||
      store.session.get('config').recruitment === 'prolific' ||
      store.session.get('config').recruitment === 'otherLabs') &&
    store.session.get('config').consent,
};

export const getUserDataTimeline = [if_consent_form_roar, if_consent_form_museum, ifGetLabId, ifGetPid];
